/*
 * PROD-7828 R8 — pure request builders + the module's enum/limit mirrors for the Additional
 * payments tab. Every constant here mirrors goserver's frozen contract (additional/types.go);
 * params.test.js pins each one against the additional_enums fixture, so a backend
 * regeneration that changes a code, a limit or a policy default fails the suite instead of the tab.
 */
import { month_label } from '../format';
import { ist_month_key } from '../shared/ist';

export const ENTRY_TYPE = { ADDITION: 'ADDITION', DEDUCTION: 'DEDUCTION' };

// ADJUSTMENT (A-6c): a system-posted delta on a closed month's reference "ADJ:YYYY-MM" — never
// editable/removable by Ops (server sends no edit/remove in editable.actions).
export const SOURCE = { MANUAL: 'MANUAL', CSV: 'CSV', SYSTEM: 'SYSTEM', ADJUSTMENT: 'ADJUSTMENT' };

export const LIST_STATUS = { ACTIVE: 'ACTIVE', REMOVED: 'REMOVED', ALL: 'ALL' };

export const ROW_ACTION = { EDIT: 'edit', REMOVE: 'remove', WAIVE: 'waive' };

// additional/types.go ListSortKeys; "date" is the server default (undated rows last either way).
export const LIST_SORTS = [
  'added_by',
  'amount',
  'category',
  'date',
  'source',
  'status',
  'teacher_name',
  'type'
];
export const SORT_DIRS = ['asc', 'desc'];
export const DEFAULT_SORT = 'date';
export const DEFAULT_DIR = 'asc';

// Ops rows are additions; the negative row is the SYSTEM carry-forward written at month close (D13).
// A reopen reverses it by removing it as ACTOR_REOPEN (CD-112: bookkeeping, not an Ops waiver).
export const CARRY_FORWARD = {
  SOURCE: SOURCE.SYSTEM,
  ENTRY_TYPE: ENTRY_TYPE.DEDUCTION,
  CATEGORY: 'PREV_MONTH_PAYMENT_ADJUSTMENT',
  REFERENCE_PREFIX: 'CF:',
  ACTOR_REOPEN: 'system:month_reopen'
};

export const WARNING = {
  ENTRY_DATE_OUTSIDE_PAY_MONTH: 'ENTRY_DATE_OUTSIDE_PAY_MONTH',
  MONTH_WILL_GO_NEGATIVE: 'MONTH_WILL_GO_NEGATIVE',
  TEACHER_HAS_NO_BASE_RATE: 'TEACHER_HAS_NO_BASE_RATE',
  RECOMPUTE_FAILED: 'RECOMPUTE_FAILED',
  ROUTED_TO_CURRENT_MONTH: 'ROUTED_TO_CURRENT_MONTH'
};

// Every code the additional endpoints return; adapters.test pins this against the goserver source.
export const ERROR = {
  INVALID_REQUEST: 'INVALID_REQUEST',
  INVALID_PAY_MONTH: 'INVALID_PAY_MONTH',
  ENTRY_TYPE_NOT_ALLOWED: 'ENTRY_TYPE_NOT_ALLOWED',
  INVALID_CLIENT_REQUEST_ID: 'INVALID_CLIENT_REQUEST_ID',
  IMMUTABLE_FIELD: 'IMMUTABLE_FIELD',
  INVALID_TEACHER_QUERY: 'INVALID_TEACHER_QUERY',
  BAD_SORT: 'BAD_SORT',
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  AMOUNT_ABOVE_CAP: 'AMOUNT_ABOVE_CAP',
  INVALID_AMOUNT: 'INVALID_AMOUNT',
  INVALID_ENTRY_DATE: 'INVALID_ENTRY_DATE',
  INVALID_REFERENCE_ID: 'INVALID_REFERENCE_ID',
  RESERVED_REFERENCE_ID: 'RESERVED_REFERENCE_ID',
  NOTE_REQUIRED: 'NOTE_REQUIRED',
  REASON_REQUIRED: 'REASON_REQUIRED',
  UNKNOWN_CATEGORY: 'UNKNOWN_CATEGORY',
  SYSTEM_CATEGORY_NOT_SELECTABLE: 'SYSTEM_CATEGORY_NOT_SELECTABLE',
  PAY_MONTH_OUTSIDE_WINDOW: 'PAY_MONTH_OUTSIDE_WINDOW',
  MONTH_CLOSED: 'MONTH_CLOSED',
  MONTH_NOT_OPEN: 'MONTH_NOT_OPEN',
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  ALREADY_REMOVED: 'ALREADY_REMOVED',
  SYSTEM_ENTRY_NOT_EDITABLE: 'SYSTEM_ENTRY_NOT_EDITABLE',
  DUPLICATE_REFERENCE: 'DUPLICATE_REFERENCE',
  POSSIBLE_DUPLICATE: 'POSSIBLE_DUPLICATE',
  CONFIRM_REQUIRED: 'CONFIRM_REQUIRED',
  IDEMPOTENCY_MISMATCH: 'IDEMPOTENCY_MISMATCH',
  TEACHER_NOT_FOUND: 'TEACHER_NOT_FOUND',
  ENTRY_NOT_FOUND: 'ENTRY_NOT_FOUND',
  SESSION_NOT_OWNED_BY_TEACHER: 'SESSION_NOT_OWNED_BY_TEACHER',
  PREVIOUS_MONTH_NO_RECORD: 'PREVIOUS_MONTH_NO_RECORD',
  FORBIDDEN: 'FORBIDDEN',
  LOOKUP_FAILED: 'LOOKUP_FAILED'
};

export const LIMITS = {
  NOTE_MAX_LEN: 1024,
  REASON_MAX_LEN: 1024,
  REFERENCE_ID_MAX_LEN: 128,
  AMOUNT_MAX_DECIMALS: 2,
  PAGE_SIZE: 100,
  LIST_LIMIT_MAX: 500,
  TEACHER_QUERY_MIN_LEN: 2,
  TEACHERS_LOOKUP_MAX: 20,
  SESSIONS_LOOKUP_MAX: 50
};

// The cap and the confirm threshold are POLICY ROWS Ops can change without a deploy. /categories
// returns the effective values under `policy`, and policy_limits() reads them; these mirror
// goserver's DefaultMaxEntryINR/DefaultConfirmAboveINR and are only the FALLBACK until it answers.
export const MAX_ENTRY_INR = 500000;
export const CONFIRM_ABOVE_INR = 25000;

export const POLICY_FALLBACK = {
  max_entry_inr: MAX_ENTRY_INR,
  confirm_above_inr: CONFIRM_ABOVE_INR
};

const positive_number = (v, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

// Reads the effective limits off any response that carries them (top level or under `policy`);
// anything missing or unusable keeps the fallback, which is what the server defaults to anyway.
export const policy_limits = (source, base = POLICY_FALLBACK) => {
  const s = source && typeof source === 'object' ? source : {};
  const p = s.policy && typeof s.policy === 'object' ? s.policy : {};
  return {
    max_entry_inr: positive_number(
      s.max_entry_inr === undefined ? p.max_entry_inr : s.max_entry_inr,
      base.max_entry_inr
    ),
    confirm_above_inr: positive_number(
      s.confirm_above_inr === undefined ? p.confirm_above_inr : s.confirm_above_inr,
      base.confirm_above_inr
    )
  };
};

// goserver's reference_id validator; the CF: prefix is reserved for the system carry-forward.
export const REFERENCE_ID_RE = /^[A-Za-z0-9._:/-]{1,128}$/;

const pad = (n) => String(n).padStart(2, '0');

// 'YYYY-MM-DD' | 'YYYY-MM' -> 'YYYY-MM'; anything else -> ''.
export const month_key = (value) => {
  const m = typeof value === 'string' && value.match(/^(\d{4})-(0[1-9]|1[0-2])/);
  return m ? `${m[1]}-${m[2]}` : '';
};

// The additional endpoints parse pay_month strictly as 'YYYY-MM-01'; the shell's months are 'YYYY-MM'.
export const api_pay_month = (value) => {
  const k = month_key(value);
  return k ? `${k}-01` : '';
};

export const previous_month_key = (key) => {
  const k = month_key(key);
  if (!k) return '';
  const year = Number(k.slice(0, 4));
  const month = Number(k.slice(5, 7));
  return month === 1 ? `${year - 1}-12` : `${year}-${pad(month - 1)}`;
};

// Q15: the editable window is the current IST pay month and the previous one only.
export const pay_month_window = (now) => {
  const current = ist_month_key(now);
  return [previous_month_key(current), current];
};

export const is_pay_month_in_window = (pay_month, now) =>
  pay_month_window(now).indexOf(month_key(pay_month)) !== -1;

/*
 * The month picker's list for this tab: the shell's /months rows plus any Q15-writable month they lack
 * (/months only lists months that already have a row), newest first, in the shell's 'YYYY-MM' form.
 */
export const with_writable_months = (months, now) => {
  const list = Array.isArray(months) ? months : [];
  const listed = new Set(list.map((m) => month_key(m && m.pay_month)));
  const extra = pay_month_window(now)
    .filter((k) => !listed.has(k))
    .map((k) => ({
      pay_month: k,
      label: month_label(k),
      teacher_count: 0,
      badge: { key: '', label: '' }
    }));
  return [...list, ...extra].sort((a, b) =>
    month_key(b.pay_month).localeCompare(month_key(a.pay_month))
  );
};

const one_of = (value, allowed) => (allowed.indexOf(value) === -1 ? '' : value);

// sort/dir: additional_db.go additionalListSort ("" = the default "date" order); no header-click UI
// yet (tracked separately) — always sent so the server's default applies until one lands.
export const list_params = (filters = {}, limit = LIMITS.PAGE_SIZE, offset = 0) => ({
  pay_month: filters.pay_month,
  teacher_uuid: filters.teacher_uuid || '',
  status: one_of(filters.status, Object.values(LIST_STATUS)),
  category: filters.category || '',
  source: one_of(filters.source, Object.values(SOURCE)),
  limit: Math.min(limit || LIMITS.PAGE_SIZE, LIMITS.LIST_LIMIT_MAX),
  offset: offset || 0,
  sort: one_of(filters.sort, LIST_SORTS) || DEFAULT_SORT,
  dir: one_of(filters.dir, SORT_DIRS) || DEFAULT_DIR
});

export const teachers_lookup_params = (q) => ({ q: (q || '').trim() });

export const sessions_lookup_params = ({ teacher_uuid, pay_month, q }) => ({
  teacher_uuid,
  pay_month,
  q: (q || '').trim()
});

// ---- list view state (drafts applied on Apply, house pattern) ----

export const initial_list_state = () => ({
  month: '',
  offset: 0,
  applied: {
    teacher_uuid: '',
    category: '',
    status: LIST_STATUS.ACTIVE,
    source: '',
    sort: DEFAULT_SORT,
    dir: DEFAULT_DIR
  },
  drafts: {
    teacher_uuid: '',
    teacher_name: '',
    category: '',
    status: LIST_STATUS.ACTIVE,
    source: ''
  }
});

// A different month resets the page but keeps the filters; the same month returns the SAME object.
export const list_state_for_month = (state, pay_month) =>
  state.month === pay_month ? state : { ...state, month: pay_month, offset: 0 };

// The filter form has no sort field, so Apply carries the current sort/dir forward unchanged.
export const apply_list_drafts = (state) => ({
  ...state,
  offset: 0,
  applied: {
    teacher_uuid: state.drafts.teacher_uuid || '',
    category: state.drafts.category || '',
    status: one_of(state.drafts.status, Object.values(LIST_STATUS)) || LIST_STATUS.ACTIVE,
    source: one_of(state.drafts.source, Object.values(SOURCE)),
    sort: state.applied.sort,
    dir: state.applied.dir
  }
});
